import * as React from "react";
import {useEffect, useState} from "react";
import {Dialog, DialogTitle, DialogContent, DialogActions, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import api from "../../../api/api";
import {TherapyInvite} from "../therapy-invite";
import YellowButton from "../../../common/buttons/YellowButton";
import SecondaryButton from "../../../common/buttons/SecondaryButton";

interface EditInviteProps {
    invite: TherapyInvite | null;
    open: boolean;
    setOpen: (open: boolean) => void;
    onSaved: () => void;
}

// Lets the therapist rename a session invite and edit its description (owner
// feedback: "Brakuje pola Edycji gdzie mogę zmienić nazwę i opis").
// Persists via PATCH invites/:id (InviteUpdateDto accepts inviteeName/comment).
const EditInvite: React.FC<EditInviteProps> = ({invite, open, setOpen, onSaved}) => {
    const {t} = useTranslation();
    const [inviteeName, setInviteeName] = useState("");
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);

    // Prefill from the selected invite whenever the dialog opens for a new row.
    useEffect(() => {
        if (invite && open) {
            setInviteeName(invite.inviteeName || "");
            setComment(invite.comment || "");
        }
    }, [invite, open]);

    const handleClose = () => setOpen(false);

    const handleSave = async () => {
        if (!invite) return;
        if (!inviteeName.trim()) {
            toast.error(t("Please enter invitee name"));
            return;
        }
        setSaving(true);
        try {
            await api().patch("invites/" + invite._id, {inviteeName, comment});
            toast.success(t("Invite has been updated successfully"));
            onSaved();
            handleClose();
        } catch (e) {
            toast.error(t("Could not update the invite. Please try again."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>{t("Edit session")}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    required
                    margin="dense"
                    id="edit-invitee-name"
                    label={t("Name (label)")}
                    type="text"
                    fullWidth
                    value={inviteeName}
                    onChange={(e) => setInviteeName(e.target.value)}
                />
                <TextField
                    margin="dense"
                    id="edit-comment"
                    label={t("Patient / session description")}
                    type="text"
                    fullWidth
                    multiline
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <YellowButton onClick={handleSave} disabled={saving}>
                    {t("Save")}
                </YellowButton>
                <SecondaryButton onClick={handleClose}>
                    {t("Cancel")}
                </SecondaryButton>
            </DialogActions>
        </Dialog>
    );
};

export default EditInvite;
